import {useLogout} from '@common/auth/requests/logout';
import {useLocalizedUrl} from '@common/custom-page/use-localized-url';
import {Button} from '@ui/buttons/button';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {Trans} from '@ui/i18n/trans';
import {KeyboardArrowLeftIcon} from '@ui/icons/material/KeyboardArrowLeft';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {useSettings} from '@ui/settings/use-settings';
import {createBrowserRouter} from 'react-router';
import {
  useAccountDeletionStatus,
  useCancelAccountDeletion,
} from './use-account-deletion';

export const accountDeletionScheduledRouter = createBrowserRouter([
  {
    path: '*',
    element: <AccountDeletionScheduledPage />,
    hydrateFallbackElement: <FullPageLoader screen />,
  },
]);

export function AccountDeletionScheduledPage() {
  const {data, isLoading} = useAccountDeletionStatus();
  const cancel = useCancelAccountDeletion();
  const logout = useLogout();
  const {
    branding: {logo_light, logo_dark, site_name},
    mail,
  } = useSettings();
  const contactUrl = useLocalizedUrl('contact');
  const isDarkMode = useIsDarkMode();
  const logoSrc = isDarkMode ? logo_light : logo_dark;

  if (isLoading) {
    return <FullPageLoader screen />;
  }

  const scheduledDeleteAt = data?.scheduled_delete_at;

  return (
    <div className="flex min-h-screen w-screen bg-alt p-24">
      <div className="mx-auto mt-40 max-w-520">
        <Button
          variant="outline"
          onClick={() => logout.mutate()}
          startIcon={<KeyboardArrowLeftIcon />}
          size="xs"
          className="mb-54 mr-auto"
        >
          <Trans message="Logout" />
        </Button>
        {logoSrc && (
          <img
            src={logoSrc}
            alt="Site logo"
            className="mx-auto mb-44 block h-42 w-auto"
          />
        )}
        <div className="text-center">
          <h1 className="mb-24 text-3xl">
            <Trans message="Your account is scheduled for deletion" />
          </h1>
          {scheduledDeleteAt ? (
            <p className="mb-16 text-base">
              <Trans
                message="Your :name account will be permanently deleted on :date. Your files, share links, and workspace access are blocked until then."
                values={{
                  name: site_name,
                  date: <FormattedDate date={scheduledDeleteAt} />,
                }}
              />
            </p>
          ) : (
            <p className="mb-16 text-base">
              <Trans
                message="Your :name account is scheduled for permanent deletion. Your files, share links, and workspace access are blocked until then."
                values={{name: site_name}}
              />
            </p>
          )}
          <p className="mb-32 text-sm text-muted">
            {data?.deletion_initiated_by === 'admin' ? (
              <Trans message="This deletion was scheduled by an administrator and can only be cancelled by an administrator. Please contact support if you think this is a mistake." />
            ) : (
              <Trans message="If this was in error, you can cancel the deletion now." />
            )}
          </p>
          <div className="flex justify-center gap-12">
            {data?.deletion_initiated_by !== 'admin' && (
              <Button
                variant="flat"
                color="primary"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate()}
              >
                <Trans message="Cancel deletion" />
              </Button>
            )}
            {mail?.contact_page_address && (
              <Button variant="text" elementType="a" href={contactUrl}>
                <Trans message="Contact support" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
